/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, August 24th 2022, 1:26:08 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { SessionExpiredNotification, useUserContext } from "authentication";
import { PermissionDeniedNotification } from "authorization";
import { APIRequestState, STATUS_CODES } from "lib/api/types";
import { ErrorFallback } from "ui-widgets/errors/ErrorFallback";
import { useAPIFetchUserDetails } from "users";

interface IAPIErrorHandlerContext {
  handleAPIResponseCode: (httpResponseStatusCode: APIRequestState["httpResponseStatusCode"]) => void;
}

const APIErrorHandlerContext = React.createContext<IAPIErrorHandlerContext>({
  handleAPIResponseCode: () => {},
});

export const APIErrorHandler = ({ children }) => {
  const { setSessionIsAuthenticated } = useUserContext();
  const [httpResponseStatusCode, setHttpResponseStatusCode] =
    React.useState<APIRequestState["httpResponseStatusCode"]>(0);

  const handleAPIResponseCode = (httpResponseStatusCode: APIRequestState["httpResponseStatusCode"]) => {
    setHttpResponseStatusCode(httpResponseStatusCode);
  };

  const {
    apiItem: user,
    fetchItem: fetchUserDetails,
    httpResponseStatusCode: userFetchStatusCode,
  } = useAPIFetchUserDetails();

  React.useEffect(() => {
    if (httpResponseStatusCode === STATUS_CODES.PermissionDenied) fetchUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [httpResponseStatusCode]);

  React.useEffect(() => {
    if (!userFetchStatusCode) return;
    setSessionIsAuthenticated(userFetchStatusCode !== STATUS_CODES.PermissionDenied);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFetchStatusCode]);

  const renderContent = () => {
    if (httpResponseStatusCode >= STATUS_CODES.ServerError) return <ErrorFallback />;

    if (userFetchStatusCode === STATUS_CODES.OK && user)
      return (
        <>
          {children}
          <PermissionDeniedNotification username={user.username} email={user.email} />
        </>
      );

    if (userFetchStatusCode)
      return (
        <>
          {children}
          <SessionExpiredNotification />
        </>
      );
    return children;
  };

  return (
    <APIErrorHandlerContext.Provider value={{ handleAPIResponseCode }}>
      {renderContent()}
    </APIErrorHandlerContext.Provider>
  );
};

export const useAPIErrorHandler = () => React.useContext(APIErrorHandlerContext);
